import { useEffect, useMemo, useRef, useState } from "react";
import { Mic, MicOff, Loader2, Sparkles, Trash2, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface VoiceInputButtonProps {
  /**
   * Aktueller Textareawert. Erkannte Sätze werden an die aktuelle Cursor-
   * position bzw. ans Ende des Textfeldes angehängt; gleichzeitig vom Nutzer
   * eingetippter Text bleibt erhalten, weil wir den Diff immer gegen den
   * jeweils aktuellsten Wert ausführen.
   */
  value: string;
  onChange: (next: string) => void;
  /**
   * Optional: testid-Präfix, damit beide Buttons (Notiz/Foto) eindeutig
   * angesprochen werden können.
   */
  testIdPrefix?: string;
  /** Sprache für die Transkription. Default: de-DE. */
  lang?: string;
  /** Optional: button text neben dem Mikro-Icon. */
  label?: string;
  /**
   * Optionaler Kurz-Kontext, der an die Cleanup-Route weitergereicht wird,
   * damit die KI den Schreibstil passend wählen kann (z. B. "project-note"
   * oder "photo-description"). Rückwärtskompatibel — bleibt das Feld leer,
   * verhält sich der Server wie bisher.
   */
  cleanupContext?: string;
}

type RecorderStatus = "idle" | "recording" | "uploading" | "cleaning";

// Maximale Aufnahme-Dauer in Sekunden. Verhindert versehentliche Endlos-
// Aufnahmen und respektiert das Whisper-Upload-Limit von ~25 MB.
const MAX_RECORD_SECONDS = 5 * 60;
// Sehr kurze Aufnahmen (versehentliches Antippen) werden ignoriert.
const MIN_RECORD_MS = 500;

function pickMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  // Reihenfolge nach Whisper-Kompatibilität & Dateigröße. webm/opus ist auf
  // Chrome/Android Standard, mp4/aac wird von iOS-Safari produziert.
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/ogg;codecs=opus",
    "audio/ogg",
  ];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      /* noop */
    }
  }
  return "";
}

function formatDuration(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Mikro-Button im WhatsApp-/ChatGPT-Stil:
 *   1. Tippen startet `MediaRecorder` (verlangt Mikro-Berechtigung).
 *   2. Nochmal tippen → Aufnahme stoppt, Audio-Blob wird zum Server geschickt
 *      und dort von OpenAI Whisper transkribiert.
 *   3. Der Rohtext geht anschließend an `/api/text/cleanup` (Claude), wird
 *      aufgeräumt und an der Cursor-Position bzw. am Ende des bisherigen
 *      Inhalts eingefügt.
 *
 * Wichtige Eigenschaften:
 * - Aufnahme läuft komplett lokal weiter, auch ohne Netz – erst beim Stopp
 *   wird hochgeladen. Bei Netzfehler bleibt der bisherige Textwert erhalten.
 * - Die Mikro-Spur wird nach dem Stopp explizit freigegeben, damit kein
 *   roter Aufnahme-Indikator im Browser hängenbleibt.
 * - Beim Unmount werden laufende Aufnahmen, Streams und Requests sauber
 *   abgebrochen.
 */
export function VoiceInputButton({
  value,
  onChange,
  testIdPrefix = "voice-input",
  lang = "de-DE",
  label = "Diktieren",
  cleanupContext,
}: VoiceInputButtonProps) {
  const { toast } = useToast();
  const supported = useMemo(() => {
    if (typeof window === "undefined") return false;
    if (typeof MediaRecorder === "undefined") return false;
    if (!navigator.mediaDevices?.getUserMedia) return false;
    return !!pickMimeType();
  }, []);

  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [elapsedSec, setElapsedSec] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const startedAtRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);
  const maxDurationTimerRef = useRef<number | null>(null);
  const discardRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const latestValueRef = useRef<string>(value);
  const busyRef = useRef(false);

  useEffect(() => {
    latestValueRef.current = value;
  }, [value]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanupRecorderResources();
      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch {
          /* noop */
        }
        abortRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cleanupRecorderResources() {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (maxDurationTimerRef.current !== null) {
      window.clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") {
      try {
        rec.stop();
      } catch {
        /* noop */
      }
    }
    recorderRef.current = null;
    const stream = streamRef.current;
    if (stream) {
      try {
        stream.getTracks().forEach((t) => t.stop());
      } catch {
        /* noop */
      }
      streamRef.current = null;
    }
    chunksRef.current = [];
  }

  async function startRecording() {
    if (!supported) {
      toast({
        variant: "destructive",
        description:
          "Dieser Browser unterstützt keine Audioaufnahme. Bitte Chrome (Android) oder Safari (iOS) verwenden.",
      });
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err: unknown) {
      const name = err instanceof Error ? err.name : "";
      let msg = "Mikrofon konnte nicht gestartet werden.";
      if (name === "NotAllowedError" || name === "SecurityError") {
        msg = "Mikrofon-Zugriff verweigert. Bitte in den Browser-Einstellungen erlauben.";
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        msg = "Kein Mikrofon gefunden.";
      }
      toast({ variant: "destructive", description: msg });
      return;
    }

    const mimeType = pickMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
    } catch (err) {
      stream.getTracks().forEach((t) => t.stop());
      toast({
        variant: "destructive",
        description:
          err instanceof Error
            ? `Aufnahme konnte nicht gestartet werden: ${err.message}`
            : "Aufnahme konnte nicht gestartet werden.",
      });
      return;
    }

    chunksRef.current = [];
    discardRef.current = false;
    streamRef.current = stream;
    recorderRef.current = recorder;

    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
    };
    recorder.onerror = () => {
      toast({
        variant: "destructive",
        description: "Aufnahme fehlgeschlagen — bitte erneut versuchen.",
      });
      discardRef.current = true;
      try {
        recorder.stop();
      } catch {
        /* noop */
      }
    };
    recorder.onstop = () => {
      const stopAt = Date.now();
      const duration = stopAt - startedAtRef.current;
      const collected = chunksRef.current.slice();
      const usedMime = recorder.mimeType || mimeType || "audio/webm";
      // Stream UND Tracks freigeben, damit der rote Aufnahme-Indikator
      // im Browser/OS verschwindet.
      const stream = streamRef.current;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      recorderRef.current = null;
      chunksRef.current = [];
      if (tickRef.current !== null) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
      if (maxDurationTimerRef.current !== null) {
        window.clearTimeout(maxDurationTimerRef.current);
        maxDurationTimerRef.current = null;
      }

      if (!mountedRef.current) return;

      if (discardRef.current) {
        setStatus("idle");
        setElapsedSec(0);
        return;
      }
      if (duration < MIN_RECORD_MS || collected.length === 0) {
        // Versehentliches Antippen → still in den Ausgangszustand zurück.
        setStatus("idle");
        setElapsedSec(0);
        return;
      }

      const blob = new Blob(collected, { type: usedMime });
      void uploadAndProcess(blob, usedMime);
    };

    try {
      recorder.start();
    } catch (err) {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      recorderRef.current = null;
      toast({
        variant: "destructive",
        description:
          err instanceof Error
            ? `Aufnahme konnte nicht gestartet werden: ${err.message}`
            : "Aufnahme konnte nicht gestartet werden.",
      });
      return;
    }

    startedAtRef.current = Date.now();
    setStatus("recording");
    setElapsedSec(0);
    tickRef.current = window.setInterval(() => {
      if (!mountedRef.current) return;
      const sec = Math.floor((Date.now() - startedAtRef.current) / 1000);
      setElapsedSec(sec);
    }, 250);
    maxDurationTimerRef.current = window.setTimeout(() => {
      if (!mountedRef.current) return;
      toast({
        description: "Aufnahme erreicht 5 Minuten — wird automatisch gestoppt.",
      });
      stopRecording(false);
    }, MAX_RECORD_SECONDS * 1000);
  }

  function stopRecording(discard: boolean) {
    const rec = recorderRef.current;
    if (!rec) return;
    discardRef.current = discard;
    if (rec.state !== "inactive") {
      try {
        rec.stop();
      } catch {
        /* noop */
      }
    }
  }

  async function uploadAndProcess(blob: Blob, mimeType: string) {
    setStatus("uploading");
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const transcript = await transcribe(blob, mimeType, ctrl.signal);
      if (!mountedRef.current) return;
      const trimmed = transcript.trim();
      if (!trimmed) {
        toast({
          description: "Keine Sprache erkannt — bitte nochmal versuchen.",
        });
        setStatus("idle");
        setElapsedSec(0);
        return;
      }
      setStatus("cleaning");
      let cleaned = trimmed;
      try {
        cleaned = await cleanup(trimmed, ctrl.signal);
      } catch (cleanupErr) {
        // Cleanup ist optional — bei Fehler den Whisper-Rohtext einfügen
        // und eine kurze Info zeigen. Dadurch geht kein Diktat verloren.
        if (!mountedRef.current) return;
        const msg =
          cleanupErr instanceof Error
            ? cleanupErr.message
            : "Aufräumen nicht möglich";
        toast({
          variant: "destructive",
          description: `Automatisches Aufräumen hat nicht geklappt (${msg}) — Rohtext wurde eingefügt.`,
        });
      }
      if (!mountedRef.current) return;
      insertText(cleaned);
      setStatus("idle");
      setElapsedSec(0);
    } catch (err: unknown) {
      const name = err instanceof Error ? err.name : "";
      if (name === "AbortError") return;
      if (!mountedRef.current) return;
      const msg = err instanceof Error ? err.message : "Unbekannter Fehler";
      toast({
        variant: "destructive",
        description: `Diktat fehlgeschlagen: ${msg}`,
      });
      setStatus("idle");
      setElapsedSec(0);
    } finally {
      if (abortRef.current === ctrl) abortRef.current = null;
    }
  }

  async function transcribe(blob: Blob, mimeType: string, signal: AbortSignal): Promise<string> {
    // Multipart-Upload mit Feldname `audio`. Der Browser setzt dann den
    // korrekten `Content-Type: multipart/form-data; boundary=…` selbst —
    // bitte hier KEINEN Content-Type-Header von Hand setzen.
    const fileExt = (mimeType.includes("mp4") || mimeType.includes("m4a"))
      ? "m4a"
      : mimeType.includes("ogg")
        ? "ogg"
        : mimeType.includes("mpeg") || mimeType.includes("mp3")
          ? "mp3"
          : mimeType.includes("wav")
            ? "wav"
            : "webm";
    const file = new File([blob], `dictation.${fileExt}`, {
      type: mimeType || "audio/webm",
    });
    const form = new FormData();
    form.append("audio", file);
    form.append("language", lang);
    const res = await fetch("/api/audio/transcribe", {
      method: "POST",
      body: form,
      credentials: "include",
      signal,
    });
    if (!res.ok) {
      let detail = "";
      try {
        const j = (await res.json()) as { message?: string };
        detail = j?.message ?? "";
      } catch {
        /* noop */
      }
      throw new Error(detail || `HTTP ${res.status}`);
    }
    const json = (await res.json()) as { text?: string };
    return (json.text ?? "").toString();
  }

  async function cleanup(text: string, signal: AbortSignal): Promise<string> {
    const res = await fetch("/api/text/cleanup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        text,
        language: lang,
        ...(cleanupContext ? { context: cleanupContext } : {}),
      }),
      credentials: "include",
      signal,
    });
    if (!res.ok) {
      let detail = "";
      try {
        const j = (await res.json()) as { message?: string };
        detail = j?.message ?? "";
      } catch {
        /* noop */
      }
      throw new Error(detail || `HTTP ${res.status}`);
    }
    const json = (await res.json()) as { cleaned?: string };
    const cleaned = (json.cleaned ?? "").trim();
    if (!cleaned) throw new Error("Leere KI-Antwort");
    return cleaned;
  }

  function insertText(snippet: string) {
    const base = latestValueRef.current;
    // Versuchen, den aktiv fokussierten Textbereich zu finden, um an der
    // Cursor-Position einzufügen. Das funktioniert für die zwei mobilen
    // Diktat-Felder (Projektnotiz, Foto-Beschreibung) wie erwartet, weil der
    // Nutzer den Mikro-Knopf direkt unter dem zugehörigen Feld antippt.
    const active = typeof document !== "undefined" ? document.activeElement : null;
    const isText =
      active instanceof HTMLTextAreaElement || active instanceof HTMLInputElement;
    if (isText) {
      const el = active as HTMLTextAreaElement | HTMLInputElement;
      // Nur einfügen, wenn das Feld unseren aktuellen `value` als Inhalt hat;
      // andernfalls am Ende anhängen, um nicht im falschen Feld zu landen.
      if ((el.value ?? "") === base) {
        const start = el.selectionStart ?? base.length;
        const end = el.selectionEnd ?? base.length;
        const before = base.slice(0, start);
        const after = base.slice(end);
        const needsLeadingSpace =
          before.length > 0 && !/[\s\n]$/.test(before) && !/^\s/.test(snippet);
        const needsTrailingSpace =
          after.length > 0 && !/^[\s\n]/.test(after) && !/\s$/.test(snippet);
        const insertion =
          (needsLeadingSpace ? " " : "") + snippet + (needsTrailingSpace ? " " : "");
        const next = before + insertion + after;
        latestValueRef.current = next;
        onChange(next);
        return;
      }
    }
    const needsSep =
      base.length > 0 && !base.endsWith(" ") && !base.endsWith("\n");
    const next = base + (needsSep ? " " : "") + snippet;
    latestValueRef.current = next;
    onChange(next);
  }

  function handleToggle() {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      if (status === "idle") {
        void startRecording();
      } else if (status === "recording") {
        stopRecording(false);
      }
      // Während uploading/cleaning ignorieren wir Klicks auf den Hauptknopf;
      // der Discard-Button ist während Aufnahme separat verfügbar.
    } finally {
      // Kurze synchrone Sperre, damit Doppel-Tap nicht direkt re-toggelt.
      window.setTimeout(() => {
        busyRef.current = false;
      }, 250);
    }
  }

  function handleDiscard() {
    if (status !== "recording") return;
    stopRecording(true);
  }

  const disabled = !supported || status === "uploading" || status === "cleaning";
  const variant: "destructive" | "outline" =
    status === "recording" ? "destructive" : "outline";

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-stretch gap-2">
        <Button
          type="button"
          onClick={handleToggle}
          disabled={disabled}
          variant={variant}
          className="flex-1 py-3 h-auto gap-2"
          data-testid={`${testIdPrefix}-toggle`}
          aria-pressed={status === "recording"}
          aria-busy={status === "uploading" || status === "cleaning"}
          title={
            !supported
              ? "Dieser Browser unterstützt keine Audioaufnahme. Bitte Chrome (Android) oder Safari (iOS) verwenden."
              : status === "uploading"
                ? "Wird transkribiert …"
                : status === "cleaning"
                  ? "Text wird aufgeräumt …"
                  : status === "recording"
                    ? "Aufnahme stoppen"
                    : "Sprachaufnahme starten"
          }
        >
          {status === "uploading" ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Wird transkribiert …
            </>
          ) : status === "cleaning" ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Text wird aufgeräumt …
            </>
          ) : status === "recording" ? (
            <>
              <span className="relative flex h-3 w-3" aria-hidden="true">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
              </span>
              <Square className="w-5 h-5" />
              <span>
                Aufnahme läuft – {formatDuration(elapsedSec)} – tippen zum Stoppen
              </span>
            </>
          ) : !supported ? (
            <>
              <MicOff className="w-5 h-5" />
              Sprachaufnahme nicht verfügbar
            </>
          ) : (
            <>
              <Mic className="w-5 h-5" />
              {label}
              <Sparkles className="w-4 h-4 ml-1 opacity-60" />
            </>
          )}
        </Button>
        {status === "recording" && (
          <Button
            type="button"
            onClick={handleDiscard}
            variant="outline"
            className="py-3 h-auto px-3"
            data-testid={`${testIdPrefix}-discard`}
            title="Aufnahme verwerfen"
            aria-label="Aufnahme verwerfen"
          >
            <Trash2 className="w-5 h-5" />
          </Button>
        )}
      </div>
    </div>
  );
}
